<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="1100px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        icon 
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>
    
    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Office Location"
      text="Edit Office Location"
      class="mb-0"
    >
      <form v-if="dialog">
        <v-container>
          <v-row>
            <v-col
              cols="6"
              class="py-0"
            >
              <v-text-field
                v-model="localOfficeLocation.name"
                v-validate="'required'"
                :error-messages="errors.collect('name')"
                data-vv-name="name"                
                label="Name"
              />
            </v-col>

            <v-col
              cols="3"
              class="pl-3 py-0 mt-n1"
            >
              <v-text-field
                v-model="localOfficeLocation.latitude"
                v-validate="'required'"
                :error-messages="errors.collect('latitude')"
                data-vv-name="latitude"                
                label="Latitude"
                type="number"
                @keydown.native="['e', 'E', '+'].includes($event.key) && $event.preventDefault()"
              />
            </v-col>

            <v-col
              cols="3"
              class="pl-3 py-0 mt-n1"
            >
              <v-text-field
                v-model="localOfficeLocation.longitude"
                v-validate="'required'"
                :error-messages="errors.collect('longitude')"
                data-vv-name="longitude"                
                label="Longitude"
                type="number"
                @keydown.native="['e', 'E', '+'].includes($event.key) && $event.preventDefault()"
              />
            </v-col>

            <v-col
              cols="12"
              class="py-0 my-0"
            >
              <v-row>
                <v-col
                  cols="6"
                  class="py-0 my-0"
                >
                  <span>Acceptable radius follow radius value set in system setting? </span>
                  <v-radio-group
                    v-model="localOfficeLocation.is_follow_setting_radius"
                    class="mt-0"
                    row
                    @change="localOfficeLocation.acceptable_radius = null"
                  >
                    <v-radio
                      :label="'Yes, ' + radiusSettingValue + ' m'"
                      :value="1"
                    />
                    <v-radio
                      label="No, overwrite it"
                      :value="0"
                    /> 
                  </v-radio-group>
                </v-col>
                <v-col
                  v-if="!localOfficeLocation.is_follow_setting_radius"
                  cols="3"
                  class="py-0 my-0"
                >
                  <v-text-field
                    v-model="localOfficeLocation.acceptable_radius"
                    v-validate="'required|integer'"
                    :error-messages="errors.collect('acceptableRadius')"
                    data-vv-name="acceptableRadius"                
                    data-vv-as="acceptable radius"                
                    label="Radius value in metre"
                    type="number"
                    min="0"
                    suffix="m"
                    @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
                  />
                </v-col>
              </v-row>
            </v-col>
            
            <v-col
              cols="12"
              class="py-0"
            >
              <v-autocomplete 
                v-model="selectedCompanies"
                label="Company"
                :items="companies"
                item-text="name"
                item-value="id"  
                hide-no-data
                hide-selected
                chips
                multiple
                deletable-chips
                clearable
              />  
            </v-col>

            <v-col
              cols="12"
              class="py-0 my-0"
            >
              <v-row>
                <v-col
                  cols="6"
                  class="py-0 my-0"
                >
                  <v-text-field
                    v-model="localOfficeLocation.address_line_one"
                    v-validate="localOfficeLocation.postcode?'required':''"
                    :error-messages="errors.collect('addressLine1')"
                    data-vv-name="addressLine1"
                    data-vv-as="address"
                    label="Address Line 1"
                    class="py-0 my-0"
                    @change="localOfficeLocation.postcode && localOfficeLocation.city_id ? getCoordinatesFromAddress() : ''"
                  />
                  <v-text-field
                    v-model="localOfficeLocation.address_line_two"
                    :disabled="!localOfficeLocation.address_line_one"
                    label="Address Line 2"
                    class="py-0 my-0"
                    @change="localOfficeLocation.postcode && localOfficeLocation.city_id ? getCoordinatesFromAddress() : ''"
                  />
                  <v-text-field
                    v-model="localOfficeLocation.address_line_three"
                    :disabled="!localOfficeLocation.address_line_one"
                    label="Address Line 3"
                    class="py-0 my-0"
                    @change="localOfficeLocation.postcode && localOfficeLocation.city_id ? getCoordinatesFromAddress() : ''"
                  />
                  <v-autocomplete
                    v-model="localOfficeLocation.postcode"
                    v-validate="localOfficeLocation.address_line_one ? 'required' : ''"
                    :items="postcodes"
                    item-text="postcode"
                    item-value="postcode"
                    value="postcode"
                    :error-messages="errors.collect('postcode')"
                    data-vv-name="postcode"
                    data-vv-as="postcode"
                    label="Postcode"
                    class="py-0 my-0"
                    @input="postCodeOnChanged(localOfficeLocation.postcode)"
                    @change="localOfficeLocation.city_id ? getCoordinatesFromAddress() : ''"
                  />
                  <v-autocomplete
                    v-model="localOfficeLocation.city_id"
                    v-validate="localOfficeLocation.postcode?'required':''"
                    :disabled="dropdownDisabled"
                    :items="filteredCities"
                    :error-messages="errors.collect('city')"
                    data-vv-name="city"
                    data-vv-as="city"
                    item-text="name"
                    item-value="id"
                    value="id"
                    label="City"
                    class="py-0 my-0"
                    hide-selected
                    @input="cityOnChanged(localOfficeLocation.city_id)"
                    @change="getCoordinatesFromAddress()"
                  />
                  <v-autocomplete
                    v-model="localOfficeLocation.state_id"
                    v-validate="localOfficeLocation.city_id ? 'required' : ''"
                    :error-messages="errors.collect('state')"
                    data-vv-name="state"
                    data-vv-as="state"
                    disabled
                    :items="states"
                    item-text="name"
                    item-value="id"
                    value="id"
                    label="State"
                    class="py-0 my-0"
                    hide-selected
                  />
                </v-col>

                <v-col cols="6">
                  <GmapMap
                    :center="location"
                    :zoom="15"
                    map-type-id="roadmap"
                    style="height: 300px"
                    :options="{
                      zoomControl: false,
                      mapTypeControl: false,
                      scaleControl: false,
                      streetViewControl: true,
                      rotateControl: false,
                      fullscreenControl: false,
                      disableDefaultUI: false
                    }"
                  >
                    <GmapMarker
                      :position="location"
                      :clickable="true"
                      :draggable="true"
                      @dragend="getCoordinates($event.latLng)"
                    />
                  </GmapMap>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </form> 

      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary"
          class="white--text"
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  import axios from 'axios'
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      officeLocation: {
        type: Object,
        default: null,
      },
      radiusSettingValue: {
        type: String,
        default: null,
      },
    },
    data () {
      return {
        dialog: null,
        progressing: false,
        localOfficeLocation: null,
        companies: [],
        postcodes: [],
        cities: [],
        states: [],
        cityIds: [],
        selectedCompanies: [],
        dropdownDisabled: null,
      }
    },
    computed: {
      filteredCities () {
        if (this.localOfficeLocation.postcode) {
          this.postCodeOnChanged(this.localOfficeLocation.postcode)
        }
        
        if (this.cityIds.length > 0) {
          return this.cities.filter(city => {
            return this.cityIds.includes(city.id)
          })
        }
        return this.cities      
      },  
      location () {
        if (!this.officeLocation) return { lat: 0, lng: 0 }
        return {
          lat: Number((this.localOfficeLocation || {}).latitude || 0),
          lng: Number((this.localOfficeLocation || {}).longitude || 0),
        }
      },
    },
    watch: {
      dialog: function (value) {
        if (!value) return
        
        Promise.all([
          this.$api.states().list(),
          this.$api.cities().list(),
          this.$api.postcodes().list(),
          this.$api.hrisCompanies().list(),
        ])
          .then(([states, cities, postcodes, companies]) => {
            this.states = states.data
            this.cities = cities.data
            this.postcodes = _.orderBy(postcodes.data, ['postcode'], ['asc'])
            this.companies = companies.data
          })

        this.localOfficeLocation = { ...this.officeLocation }

        this.selectedCompanies = this.localOfficeLocation.office_location_companies.map(company => {
          return company.company_uid
        })
      },
    },
    methods: { 
      getCoordinatesFromAddress () {
        let axios2 = axios.create()

        delete axios2.defaults.headers.common['Authorization']
        axios2.post('https://maps.googleapis.com/maps/api/geocode/json?address=' + this.constructAddress() + '&key=AIzaSyCdnyWg9C4vvfw6YAjyasELkFUqXya8huA')
          .then(res => {
            if (res.data.status === 'OK') {
              res.data.results.map(data => {
                this.localOfficeLocation.latitude = data.geometry.location.lat
                this.localOfficeLocation.longitude = data.geometry.location.lng
              })
            } else {
              this.$snack({ 
                color: 'error',
                message: 'GPS coordinate not found. Please mark the correct location', 
              })
            }
          })
      },
      constructAddress () {
        return this.checkStringHasComma(this.localOfficeLocation.address_line_one) + ', ' +
          (this.localOfficeLocation.address_line_two ? this.localOfficeLocation.address_line_two + ', ' : '') +
          (this.localOfficeLocation.address_line_three ? this.localOfficeLocation.address_line_three + ', ' : '') + this.localOfficeLocation.postcode + ' ' + this.getCity(this.localOfficeLocation.city_id).name + ', ' + this.getState(this.localOfficeLocation.state_id).name
      },
      checkStringHasComma (string) {
        if (string) {
          if (string.charAt(string.length - 1) === ',') {
            return string.substring(0, string.length - 1)
          }
        }
        return string
      },
      postCodeOnChanged (postcodeValue) {
        var cityID = this.postcodes.filter(p => {
          return p.postcode === postcodeValue
        })  

        this.cityIds = this.cities.filter(city => {
          return cityID.some(city2 => {
            return city2.city_id === city.id
          })
        }).map(city3 => {
          return city3.id
        })

        if (this.cityIds.length > 1) {
          this.dropdownDisabled = false
        } else {
          this.dropdownDisabled = true
          
          let cityId = (this.postcodes.find(postcode => {
            return postcode.postcode === postcodeValue
          }) || {}).city_id

          if (cityId) {
            var cityObject = this.getCity(cityId)
            if (cityObject) {
              this.localOfficeLocation.city_id = cityObject.id
              var stateObject = this.getState(cityObject.state_id)
              
              if (stateObject) {
                this.localOfficeLocation.state_id = stateObject.id
              }
            }
          }
        }
      },
      cityOnChanged (cityId) {
        var cityObject = this.getCity(cityId)
        if (cityObject) {
          var stateObject = this.getState(cityObject.state_id)
          
          if (stateObject) {
            this.localOfficeLocation.state_id = stateObject.id
          }
        }
      },
      getCity (cityId) {
        if (isNaN(parseInt(cityId))) return null
        return this.cities.find(city => {
          return city.id === parseInt(cityId)
        })
      },
      getState (stateId) {
        if (isNaN(parseInt(stateId))) return null
        return this.states.find(state => {
          return state.id === parseInt(stateId)
        })
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true
            this.$api.officeLocations().update(this.localOfficeLocation.id, this.prepareFormData())
              .then(response => {
                if (response.status === 200) {
                  this.$emit('updated')
                  this.$snack({ message: 'Office Location updated successfully' })

                  this.dialog = false
                }
              })
              .catch(() => {})
              .then(() => {
                this.progressing = false
              })
          })
      },
   
      getCoordinates (location) {
        this.localOfficeLocation.latitude = location.lat()
        this.localOfficeLocation.longitude = location.lng()
      },

      prepareFormData () {
        return {
          name: this.localOfficeLocation.name,
          latitude: this.localOfficeLocation.latitude,
          longitude: this.localOfficeLocation.longitude,
          is_follow_setting_radius: this.localOfficeLocation.is_follow_setting_radius,
          acceptable_radius: this.localOfficeLocation.acceptable_radius,
          company_uids: this.selectedCompanies,
          address_line_one: this.localOfficeLocation.address_line_one,
          address_line_two: this.localOfficeLocation.address_line_two,
          address_line_three: this.localOfficeLocation.address_line_three,
          postcode: this.localOfficeLocation.postcode,
          city_id: this.localOfficeLocation.city_id,
          state_id: this.localOfficeLocation.state_id,
        }
      },
    },
  }
</script>
